import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { Plus, Ticket as TicketIcon } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  EmptyState,
  Field,
  InlineAlert,
  Input,
  ListToolbar,
  Page,
  PageHeader,
  Pagination,
  QueryState,
  RadioGroup,
  ResultCount,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  Textarea,
  VisuallyHidden,
} from '@aeris/ui'

import { PriorityBadge, StatusBadge } from '../components/ticket-badges.tsx'
import {
  ApiError,
  createTicket,
  fetchTickets,
  type TicketSummaryType,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { PRIORITY_META, formatDateTime } from '../lib/ticket-meta.tsx'

/**
 * Chamados: o atendimento que não acaba quando a conversa fecha (F051).
 *
 * NÃO é ordem de serviço técnica — decisão do dono, já tomada
 * (OMNICHANNEL_PLAN.md, decisão 5): sem equipamento, agendamento, peças ou
 * valor. Um chamado é um ticket ligado a uma conversa (ou aberto à mão):
 * protocolo, assunto, situação, prioridade, responsável, histórico.
 *
 * A lista é a fila de trabalho — o servidor já ordena por urgência e por
 * quanto tempo o chamado espera (`listTickets`, `@aeris/database`); esta
 * tela só lê a ordem que chegou, nunca reordena por conta própria.
 *
 * O detalhe usa as MESMAS transições legais que o servidor decide de verdade
 * (`TICKET_TRANSITIONS`, `@aeris/communication`) só para saber quais botões
 * oferecer — quem decide de fato é sempre a rota, que recusa com 422 se algo
 * divergir.
 */

// 25, como todas as listas (F132). Era 20 só aqui.
const TICKET_PAGE_SIZE = 25

/* ---------------------------------------------------------------------- */

/** As quatro prioridades com o que cada uma SIGNIFICA — os textos vivem em `lib/ticket-meta.tsx`, junto das cores. */
const PRIORITY_OPTIONS = (['urgent', 'high', 'normal', 'low'] as const).map(
  (value) => ({
    value,
    label: PRIORITY_META[value].label,
    description: PRIORITY_META[value].hint,
  })
)

const createFormSchema = z.object({
  subject: z.string().trim().min(1, 'Informe o assunto').max(200),
  description: z.string().trim().max(4000),
  priority: z.enum(['low', 'normal', 'high', 'urgent']),
})

type CreateFormValues = z.infer<typeof createFormSchema>

type CreateTicketDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (ticket: TicketSummaryType) => void
}

const CreateTicketDialog = ({
  open,
  onOpenChange,
  onCreated,
}: CreateTicketDialogProps): ReactNode => {
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)
  const [priority, setPriority] =
    useState<CreateFormValues['priority']>('normal')

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateFormValues>({
    resolver: zodResolver(createFormSchema),
    defaultValues: { subject: '', description: '', priority: 'normal' },
  })

  const save = useMutation({
    mutationFn: async (values: CreateFormValues) =>
      await createTicket({
        subject: values.subject.trim(),
        description:
          values.description.trim() === '' ? null : values.description,
        priority: values.priority,
        partyId: null,
        conversationId: null,
      }),
    onSuccess: async (ticket) => {
      setFormError(null)
      reset()
      await queryClient.invalidateQueries({
        queryKey: ['communication-tickets'],
      })
      onCreated(ticket)
      onOpenChange(false)
    },
    onError: (cause: unknown) => {
      setFormError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível abrir este chamado.'
      )
    },
  })

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    save.mutate(values)
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Abrir chamado"
        description="Um atendimento que precisa continuar depois da conversa."
        className="max-w-lg"
      >
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            void onSubmit(event)
          }}
        >
          <Field label="Assunto" required error={errors.subject?.message}>
            {(field) => (
              <Input
                {...field}
                {...register('subject')}
                autoFocus
                placeholder="Cliente perguntou sobre garantia"
              />
            )}
          </Field>

          <Field
            label="Descrição"
            error={errors.description?.message}
            hint="Opcional — o que o cliente precisa, em detalhe."
          >
            {(field) => (
              <Textarea {...field} {...register('description')} rows={3} />
            )}
          </Field>

          {/*
            F061 — prioridade em `RadioGroup`, não `<select>`: são quatro
            opções que decidem a ordem da fila de trabalho, e num `<select>`
            "Urgente" e "Baixa" pesam igual até alguém abrir a lista. Cada
            uma diz o que significa, porque "alta" e "urgente" não se
            distinguem sozinhas.
          */}
          <Field label="Prioridade" error={errors.priority?.message}>
            {(field) => (
              <RadioGroup
                {...field}
                value={priority}
                onValueChange={(next) => {
                  setPriority(next)
                  setValue('priority', next, { shouldValidate: true })
                }}
                options={PRIORITY_OPTIONS}
              />
            )}
          </Field>

          {formError === null ? null : (
            <InlineAlert tone="danger">{formError}</InlineAlert>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" variant="primary" loading={isSubmitting}>
              Abrir chamado
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/* ---------------------------------------------------------------------- */

/* ---------------------------------------------------------------------- */

export const ChamadosPage = (): ReactNode => {
  const navigate = useNavigate()
  const { allows } = useAuth()
  const canWrite = allows('communication.ticket.write')

  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('')
  const [priorityFilter, setPriorityFilter] = useState('')
  const [createOpen, setCreateOpen] = useState(false)

  const ticketsQuery = useQuery({
    queryKey: [
      'communication-tickets',
      { page, status: statusFilter, priority: priorityFilter },
    ],
    queryFn: async ({ signal }) =>
      await fetchTickets(
        {
          ...(statusFilter === '' ? {} : { status: statusFilter }),
          ...(priorityFilter === '' ? {} : { priority: priorityFilter }),
          limit: TICKET_PAGE_SIZE,
          offset: (page - 1) * TICKET_PAGE_SIZE,
        },
        signal
      ),
    placeholderData: (previous) => previous,
  })

  const rows = ticketsQuery.data?.tickets ?? []
  const total = ticketsQuery.data?.total ?? 0
  const filtered = statusFilter !== '' || priorityFilter !== ''

  return (
    <Page>
      <PageHeader
        title="Chamados"
        description="O atendimento que não acaba quando a conversa fecha: protocolo, situação, prioridade e histórico."
        actions={
          canWrite ? (
            <Button
              variant="primary"
              size="sm"
              leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
              onClick={() => {
                setCreateOpen(true)
              }}
            >
              Abrir chamado
            </Button>
          ) : null
        }
      />

      {/* Filtros como em toda lista (F132): o select com nome para o leitor
          de tela, sem um rótulo visível que só esta tela tinha. */}
      <ListToolbar>
        <Select
          aria-label="Situação"
          className="w-48"
          value={statusFilter}
          onChange={(event) => {
            setStatusFilter(event.target.value)
            setPage(1)
          }}
        >
          <option value="">Todas as situações</option>
          <option value="open">Aberto</option>
          <option value="in_progress">Em andamento</option>
          <option value="waiting">Aguardando</option>
          <option value="closed">Fechado</option>
        </Select>

        <Select
          aria-label="Prioridade"
          className="w-48"
          value={priorityFilter}
          onChange={(event) => {
            setPriorityFilter(event.target.value)
            setPage(1)
          }}
        >
          <option value="">Todas as prioridades</option>
          <option value="urgent">Urgente</option>
          <option value="high">Alta</option>
          <option value="normal">Normal</option>
          <option value="low">Baixa</option>
        </Select>
      </ListToolbar>

      <ResultCount
        page={page}
        pageSize={TICKET_PAGE_SIZE}
        total={total}
        loading={ticketsQuery.isPending}
        none="Nenhum chamado encontrado"
      />

      <QueryState
        isPending={ticketsQuery.isPending}
        isError={ticketsQuery.isError}
        error={ticketsQuery.error}
        onRetry={() => {
          void ticketsQuery.refetch()
        }}
        what="os chamados"
        isEmpty={rows.length === 0}
        filtered={filtered}
        onClearFilters={() => {
          setStatusFilter('')
          setPriorityFilter('')
          setPage(1)
        }}
        empty={
          <EmptyState
            icon={<TicketIcon aria-hidden="true" className="h-8 w-8" />}
            title="Nenhum chamado por aqui"
            description={
              canWrite
                ? 'Abra um chamado quando um atendimento precisar continuar depois da conversa.'
                : 'Nenhum chamado aberto no momento.'
            }
          />
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Protocolo</TableHeaderCell>
                <TableHeaderCell>Assunto</TableHeaderCell>
                <TableHeaderCell>Situação</TableHeaderCell>
                <TableHeaderCell>Prioridade</TableHeaderCell>
                <TableHeaderCell>Aberto em</TableHeaderCell>
                <TableHeaderCell>
                  <VisuallyHidden>Ações</VisuallyHidden>
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((ticket) => (
                <TableRow key={ticket.id}>
                  <TableCell>
                    <span className="font-(--font-mono) text-(length:--text-sm)">
                      {ticket.number === null
                        ? '—'
                        : `#${String(ticket.number)}`}
                    </span>
                  </TableCell>
                  <TableCell>{ticket.subject}</TableCell>
                  <TableCell>
                    <StatusBadge status={ticket.status} />
                  </TableCell>
                  <TableCell>
                    <PriorityBadge priority={ticket.priority} />
                  </TableCell>
                  <TableCell>
                    <span className="text-(length:--text-xs) text-(--text-muted)">
                      {formatDateTime(ticket.openedAt)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button asChild variant="ghost" size="sm">
                      <Link to="/chamados/$id" params={{ id: ticket.id }}>
                        Abrir
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </QueryState>

      <Pagination
        page={page}
        pageSize={TICKET_PAGE_SIZE}
        total={total}
        onPageChange={setPage}
        label="Paginação dos chamados"
      />

      {createOpen ? (
        <CreateTicketDialog
          open
          onOpenChange={setCreateOpen}
          onCreated={(ticket) => {
            // Criar leva DIRETO ao chamado novo — antes abria o diálogo de
            // detalhe por cima da lista, e o protocolo recém-nascido não
            // ficava em lugar nenhum que a pessoa pudesse copiar.
            void navigate({ to: '/chamados/$id', params: { id: ticket.id } })
          }}
        />
      ) : null}
    </Page>
  )
}
